import { useCallback, useState } from 'react';
import {
  setTovQueryData,
  useTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';

import { guestApi } from '../app/api';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import {
  BrowserHttpError,
  isNetworkUnavailableError,
} from '../lib/http/createBrowserHttpClient';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import type { GuestGalleryData } from './galleryHelpers';

export type GalleryStatus = 'loading' | 'ready' | 'error';
export type GalleryErrorKind =
  | 'no_access'
  | 'not_available'
  | 'network'
  | 'unexpected';

interface UseGuestGalleryResult {
  status: GalleryStatus;
  gallery: GuestGalleryData | null;
  reload: () => Promise<void>;
  loadMore: () => Promise<void>;
  updateGallery: (
    updater: (current: GuestGalleryData) => GuestGalleryData,
  ) => void;
  isLoadingMore: boolean;
  errorKind: GalleryErrorKind | null;
}

function resolveGalleryErrorKind(error: unknown): GalleryErrorKind {
  if (error instanceof BrowserHttpError) {
    if (error.statusCode === 401 || error.statusCode === 403) {
      return 'no_access';
    }

    if (error.statusCode === 404) {
      return 'not_available';
    }
  }

  if (isNetworkUnavailableError(error)) {
    return 'network';
  }

  return 'unexpected';
}

// Owns loading the guest gallery payload and its loading/error state, so the
// page component stays focused on presentation.
export function useGuestGallery(): UseGuestGalleryResult {
  const access = useGuestAccess();
  const queryClient = useTovQueryClient();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const galleryQueryKey =
    access.status === 'ready'
      ? guestQueryKeys.gallery(access.occasionSlug, access.guestToken)
      : guestQueryKeys.gallery('pending', 'pending');
  const galleryQuery = useTovQuery({
    queryKey: galleryQueryKey,
    queryFn: () => {
      if (access.status !== 'ready') {
        throw new Error('Guest access is not ready.');
      }

      return guestApi.getGallery(access.occasionSlug, access.guestToken);
    },
    enabled: access.status === 'ready',
  });
  const gallery = galleryQuery.data ?? null;

  const fetchGallery = useCallback(
    async (cursor?: string | null) => {
      if (access.status !== 'ready') {
        return null;
      }

      return guestApi.getGallery(access.occasionSlug, access.guestToken, {
        cursor,
      });
    },
    [access],
  );

  const reload = useCallback(async () => {
    await galleryQuery.refetch();
  }, [galleryQuery]);

  const loadMore = useCallback(async () => {
    if (!gallery?.pageInfo.nextCursor || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const response = await fetchGallery(gallery.pageInfo.nextCursor);

      if (!response) {
        return;
      }

      setTovQueryData<GuestGalleryData>(
        queryClient,
        galleryQueryKey,
        (current) => {
          if (!current) {
            return response;
          }

          const existingIds = new Set(
            current.items.map((item) => item.galleryItem.id),
          );
          const nextItems = response.items.filter(
            (item) => !existingIds.has(item.galleryItem.id),
          );

          return {
            ...current,
            items: [...current.items, ...nextItems],
            pageInfo: response.pageInfo,
          };
        },
      );
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchGallery, gallery, galleryQueryKey, isLoadingMore, queryClient]);

  const updateGallery = useCallback(
    (updater: (current: GuestGalleryData) => GuestGalleryData) => {
      setTovQueryData<GuestGalleryData>(
        queryClient,
        galleryQueryKey,
        (current) => (current ? updater(current) : current),
      );
    },
    [galleryQueryKey, queryClient],
  );

  return {
    status: galleryQuery.isLoading
      ? 'loading'
      : galleryQuery.isError
        ? 'error'
        : 'ready',
    gallery,
    reload,
    loadMore,
    updateGallery,
    isLoadingMore,
    errorKind: galleryQuery.isError
      ? resolveGalleryErrorKind(galleryQuery.error)
      : null,
  };
}
